import { html, ref, when } from '@microsoft/fast-element';
import { whiteCheckIcon, whiteErrorIcon, createIcon } from '../../../styles';
import { eventDetail } from '../../../utils/formatting';
import type { VppaModal } from './vppa-modal';
import { CONTRACT_TYPE } from '../../../utils';

export enum Labels {
  TITLE = 'New Contract',
  UPDATE_TITLE = 'Edit',
  COPY_TITLE = 'Copy',
  NEW_ENTRY_TAB = 'New Entry',
  BATCH_UPLOAD_TAB = 'Batch Upload',
  CLEAR_BUTTON = 'Clear',
  ADD_BUTTON = 'Add New Contract',
  EDIT_BUTTON = 'Edit Contract',
  UPLOAD_FILE_BUTTON = 'Upload File',
  CONFIRM_VPPA_UPLOAD_TITLE = 'Confirm Contract Upload',
  UPLOAD_FILE_CANCEL = 'Cancel',
  UPLOAD_FILE_CONFIRM = 'Confirm',
}

export enum vppaModaltabs {
  FORM = 'FORM',
  FILE_UPLOAD = 'FILE_UPLOAD',
}

export const VppaModalTemplate = html<VppaModal>`
  <right-side-modal
    ${ref('vppaModal')}
    show="${(x) => x.show}"
    @modalClosing="${(x) => x.closeModal()}"
    width="${(x) => x.width}"
    header="${(x) => x.title}"
  >
    ${when(
      (x) => !x.isFileConfirmationState && !x.isUpdateModal,
      html<VppaModal>`
        <div class="dialog-content">
          <zero-tabs class="top-level">
            <zero-tab @click=${(x) => x.selectTab(vppaModaltabs.FORM)}>
              ${(x) => Labels.NEW_ENTRY_TAB}
            </zero-tab>
            <zero-tab style="display: none" @click=${(x) => x.selectTab(vppaModaltabs.FILE_UPLOAD)}>
              ${(x) => Labels.BATCH_UPLOAD_TAB}
            </zero-tab>
            <zero-tab-panel>
              <vppa-form
                ${ref('vppaForm')}
                @formChanged="${(x, c) => (x.isFormValid = eventDetail(c))}"
              ></vppa-form>
            </zero-tab-panel>
            <zero-tab-panel>
              <upload-file
                ${ref('fileUpload')}
                @fileSelected="${(x, c) => x.fileSelected(eventDetail(c))}"
              ></upload-file>
            </zero-tab-panel>
          </zero-tabs>
        </div>
        <zero-button
          slot="buttons"
          appearance="secondary"
          @click="${(x) => x.clearForm()}"
          style="display: ${(x) => (x.selectedTab === vppaModaltabs.FORM ? '' : 'none')}"
        >
          ${(x) => Labels.CLEAR_BUTTON}
        </zero-button>
        <zero-button
          slot="buttons"
          appearance="secondary"
          ?disabled="${(x) => !x.file}"
          @click="${(x) => x.clearFileArea()}"
          style="display: ${(x) => (x.selectedTab === vppaModaltabs.FILE_UPLOAD ? '' : 'none')}"
        >
          ${(x) => Labels.CLEAR_BUTTON}
        </zero-button>

        <zero-button
          slot="buttons"
          appearance="primary"
          @click="${(x) => x.submitForm()}"
          style="display: ${(x) => (x.selectedTab === vppaModaltabs.FORM ? '' : 'none')}"
          ?disabled="${(x) => !x.isFormValid || !x.vppaForm.isAbleToExecute}"
        >
          ${createIcon('start')}${(x) => Labels.ADD_BUTTON}
        </zero-button>
        <zero-button
          slot="buttons"
          appearance="primary"
          ?disabled="${(x) => !x.file}"
          @click="${(x) => x.submitFile()}"
          style="display: ${(x) => (x.selectedTab === vppaModaltabs.FILE_UPLOAD ? '' : 'none')}"
        >
          ${(x) => Labels.UPLOAD_FILE_BUTTON}
        </zero-button>
      `,
    )}
    ${when(
      (x) => x.isFileConfirmationState && !x.isUpdateModal,
      html<VppaModal>`
        ${when(
          (x) => x.incorrectVppaObjectsUploaded?.length > 0,
          html<VppaModal>`
            <p class="errors-title">The following VPPA’s have errors:</p>
            <upload-validation
              :errorProjectList=${(x) => x.incorrectVppaObjectsUploaded}
            ></upload-validation>
          `,
        )}
        <reduced-upload-file
          @fileSelected="${(x, c) => x.fileSelected(eventDetail(c))}"
        ></reduced-upload-file>
        ${when(
          (x) => x.correctVppaObjectsUploaded.length > 0,
          html<VppaModal>`
            <p class="success-title">The following VPPA’s were uploaded successfully.</p>
            <p class="confirm-text">Please confirm the VPPA’s you would like to upload:</p>
            <validation-grid
              :objectList=${(x) => x.correctVppaObjectsUploaded}
              :columnConfigs=${(x) => x.vppaColumns}
            ></validation-grid>
          `,
        )}
        <zero-button
          slot="buttons"
          appearance="tertiary"
          class="button-length cancel-button"
          @click="${(x) => x.cancelFileUpload()}"
        >
          ${whiteErrorIcon('start')} ${(x) => Labels.UPLOAD_FILE_CANCEL}
        </zero-button>
        <zero-button
          slot="buttons"
          appearance="primary"
          class="button-length"
          @click="${(x) => x.insertVppasList()}"
          ?disabled="${(x) => x.incorrectVppaObjectsUploaded?.length > 0}"
        >
          ${whiteCheckIcon('start')} ${(x) => Labels.UPLOAD_FILE_CONFIRM}
        </zero-button>
      `,
    )}
    ${when(
      (x) => x.isUpdateModal,
      html<VppaModal>`
        <div class="dialog-form-content">
          <vppa-form
            ${ref('vppaFormUpdate')}
            @formChanged="${(x, c) => (x.isFormValid = eventDetail(c))}"
          ></vppa-form>
        </div>
        <zero-button slot="buttons" appearance="secondary" @click="${(x) => x.clearForm()}">
          ${(x) => Labels.CLEAR_BUTTON}
        </zero-button>
        <zero-button
          slot="buttons"
          appearance="primary"
          @click="${(x) => x.submitForm()}"
          style="display: ${(x) => (x.selectedTab === vppaModaltabs.FORM ? '' : 'none')}"
          ?disabled="${(x) => !x.isFormValid || !x.vppaFormUpdate.isAbleToExecute}"
        >
          ${createIcon('start')}${(x) => Labels.EDIT_BUTTON}
        </zero-button>
      `,
    )}
  </right-side-modal>
`;
